import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getProximosEventos } from '../services/apis';

const formatToday = () => {
    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const eventTimestamp = (fecha, hora) => {
    const eventDate = new Date(`${fecha || formatToday()}T${String(hora || '00:00').slice(0, 5)}:00`);
    return Number.isNaN(eventDate.getTime()) ? Number.MAX_SAFE_INTEGER : eventDate.getTime();
};

const formatShortDate = (fecha) => {
    if (!fecha) {
        return '';
    }

    const [year, month, day] = String(fecha).split('-');
    return year && month && day ? `${day}/${month}` : '';
};

const formatTime = (hora) => String(hora || '--:--').slice(0, 5);

const eventIcon = {
    comida: <RestaurantIcon fontSize="small" />,
    limpieza: <CleaningServicesIcon fontSize="small" />,
    actividad: <LocalActivityIcon fontSize="small" />,
    ropa: <CheckroomIcon fontSize="small" />,
};

export const SideTodayEvents = () => {
    const [loaded, setLoaded] = useState(false);
    const [agenda, setAgenda] = useState(null);

    useEffect(() => {
        const cargarAgenda = async () => {
            try {
                const response = await getProximosEventos(formatToday());
                if (response?.success) {
                    setAgenda(response.data);
                }
            } catch (error) {
                console.error('Error en carregar agenda lateral:', error);
            } finally {
                setLoaded(true);
            }
        };

        cargarAgenda();
    }, []);

    const eventos = useMemo(() => {
        if (!agenda) {
            return [];
        }

        const comidas = (agenda.comidas || []).map((evento) => ({
            tipo: 'comida',
            titulo: evento.comida || evento.descripcion || 'Menjar',
            detalle: evento.cocineros?.join(', ') || '',
            fecha: evento.fecha || agenda.fecha || '',
            hora: evento.hora || '',
        }));
        const limpieza = (agenda.limpieza || []).map((evento) => ({
            tipo: 'limpieza',
            titulo: evento.descripcion || 'Neteja',
            detalle: evento.limpiadores?.join(', ') || '',
            fecha: evento.fecha || agenda.fecha || '',
            hora: evento.hora || '',
        }));
        const actividades = (agenda.actividades || []).map((evento) => ({
            tipo: 'actividad',
            titulo: evento.actividad || evento.descripcion || 'Activitat',
            detalle: evento.encargado?.join(', ') || '',
            fecha: evento.fecha || agenda.fecha || '',
            hora: evento.hora || '',
        }));
        const ropa = (agenda.ropa || []).map((evento) => ({
            tipo: 'ropa',
            titulo: evento.descripcion || 'Roba',
            detalle: '',
            fecha: evento.fecha || agenda.fecha || '',
            hora: evento.hora || '',
        }));

        return [...comidas, ...limpieza, ...actividades, ...ropa]
            .sort((a, b) => eventTimestamp(a.fecha, a.hora) - eventTimestamp(b.fecha, b.hora));
    }, [agenda]);

    if (!loaded) {
        return null;
    }

    return (
        <Accordion defaultExpanded elevation={0} className="side-info-card side-section-accordion">
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                <Box className="side-info-heading">
                    <EventAvailableIcon fontSize="small" />
                    <Typography className="side-widget-title">Pr&ograve;xims</Typography>
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                {eventos.length === 0 ? (
                    <Typography className="side-empty-text">Sense esdeveniments pr&ograve;xims</Typography>
                ) : (
                    <Stack className="side-event-list" >
                        {eventos.map((evento, index) => {
                            const prefixFecha = evento.fecha && evento.fecha !== formatToday() ? `${formatShortDate(evento.fecha)} - ` : '';

                            return (
                                <Box key={`${evento.tipo}-${evento.fecha}-${evento.hora}-${index}`} className={`side-event-row side-event-${evento.tipo}`}>
                                    <Box className="side-event-icon">
                                        {eventIcon[evento.tipo] || eventIcon.actividad}
                                    </Box>
                                    <Box className="side-event-copy">
                                        <Typography className="side-event-title">{evento.titulo}</Typography>
                                        <Typography className="side-event-meta">
                                            {prefixFecha}{formatTime(evento.hora)}{evento.detalle ? ` - ${evento.detalle}` : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    );
};
